import { getSessionVoyageurLang } from "../core/voyageurLanguage"

const STATIC_DOC_PLACEHOLDER = /\{\{STATIC_DOC:([a-z0-9_]+)\}\}/g
const LOCALE_SEGMENT_IN_STATIC_URL = /^(.+\/)([a-z]{2}-[A-Z]{2}|default)(\/[^/]+\.pdf.*)$/i

function validateStaticDocUrl(url, legacyFallback) {
  if (url && !url.match(/^https?:\/\//i)) {
    console.warn(`[getStaticDocUrl] URL suspecte: ${url}`)
    return legacyFallback
  }
  return url
}

/**
 * Dérive l'URL d'un autre locale en remplaçant le segment BCP-47 dans le chemin NAS.
 * ex. .../mediamarkt_be/fr-BE/privacy.pdf → .../mediamarkt_be/nl-BE/privacy.pdf
 * @param {string} url
 * @param {string} localeTag
 */
function deriveStaticDocUrlForLocale(url, localeTag) {
  if (!url || !localeTag) return null
  const match = url.match(LOCALE_SEGMENT_IN_STATIC_URL)
  if (!match || match[2] === localeTag) return url
  return `${match[1]}${localeTag}${match[3]}`
}

/**
 * Locale BCP-47 active pour les documents statiques (sélecteur voyageur > commande > défaut tunnel).
 * @param {object} tunnelData
 */
export function getCurrentVoyageurLocaleTag(tunnelData) {
  return getSessionVoyageurLang() || tunnelData?.data?.order?.orderLanguageTag || tunnelData?.lang || null
}

/**
 * URLs PDF résolues côté voyager-script (HEAD + fallback default).
 * Priorité : staticDocumentUrlsByLang[langue courante] > dérivation depuis staticDocumentUrls > legacyFallback.
 * @param {object} tunnelData
 * @param {string} key ex. privacy, contract
 * @param {string} legacyFallback URL historique si la résolution n'a pas eu lieu
 */
export function getStaticDocUrl(tunnelData, key, legacyFallback) {
  const localeTag = getCurrentVoyageurLocaleTag(tunnelData)
  const byLang = localeTag ? tunnelData?.staticDocumentUrlsByLang?.[localeTag]?.[key] : null
  if (byLang) return validateStaticDocUrl(byLang, legacyFallback)

  const cached = tunnelData?.staticDocumentUrls?.[key]
  if (cached && localeTag) {
    const derived = deriveStaticDocUrlForLocale(cached, localeTag)
    if (derived) return validateStaticDocUrl(derived, legacyFallback)
  }

  if (legacyFallback && localeTag) {
    const derivedFallback = deriveStaticDocUrlForLocale(legacyFallback, localeTag)
    if (derivedFallback) return validateStaticDocUrl(derivedFallback, legacyFallback)
  }

  return validateStaticDocUrl(cached || legacyFallback, legacyFallback)
}

/**
 * Remplace les placeholders {{STATIC_DOC:key}} par l'URL de la langue courante.
 * @param {string} str
 * @param {object} tunnelData
 */
export function resolveStaticDocPlaceholders(str, tunnelData) {
  if (typeof str !== "string" || !str) return str
  return str.replace(STATIC_DOC_PLACEHOLDER, (match, key) => getStaticDocUrl(tunnelData, key, match))
}

/**
 * Remplace dans le HTML les URLs PDF d'autres langues par celles de la langue courante.
 * @param {string} html
 * @param {object} tunnelData
 */
export function substituteStaticDocUrlsInHtml(html, tunnelData) {
  if (typeof html !== "string" || !html) return html

  const localeTag = getCurrentVoyageurLocaleTag(tunnelData)
  if (!localeTag) return html

  if (tunnelData?.staticDocumentUrlsByLang) {
    const currentUrls = tunnelData.staticDocumentUrlsByLang[localeTag]
    if (currentUrls) {
      let result = html
      for (const [docKey, currentUrl] of Object.entries(currentUrls)) {
        if (!currentUrl) continue
        for (const langUrls of Object.values(tunnelData.staticDocumentUrlsByLang)) {
          const otherUrl = langUrls?.[docKey]
          if (otherUrl && otherUrl !== currentUrl) {
            result = result.split(otherUrl).join(currentUrl)
          }
        }
      }
      return result
    }
  }

  // Repli : remplace le segment locale dans les chemins NAS statiques (panier, HTML patché, etc.)
  return html.replace(
    /(https:\/\/static[-\w]*\.evollis\.com\/[^/"'\s]+)\/([a-z]{2}-[A-Z]{2}|default)(\/[^"'\s]+\.pdf)/gi,
    (_, prefix, _oldLocale, suffix) => `${prefix}/${localeTag}${suffix}`
  )
}

/**
 * Applique placeholders + substitution multi-langue sur un fragment HTML/texte.
 * @param {string} content
 * @param {object} tunnelData
 */
export function resolveStaticDocContent(content, tunnelData) {
  if (typeof content !== "string" || !content) return content
  return substituteStaticDocUrlsInHtml(resolveStaticDocPlaceholders(content, tunnelData), tunnelData)
}
